import { Button } from "@admin/components/atoms/Button";
import { Input } from "@admin/components/atoms/Input";
import { Info } from "@admin/components/molecules/Person";
import { DayPicker } from "@admin/components/molecules/Picker";
import { DataTable, DataTableColumn } from "@admin/components/molecules/Table";
import {
  calculateShiftDuration,
  CompanyLocation,
  dateToISO,
  formatDuration,
  getDayStartEnd,
  getHoursAndMinutes,
  getLocationUsers,
  getNormalizedStartEnd,
  getShiftsByDate,
  Shift,
  UpdateBulkShift,
  updateBulkShift,
  UpdateShiftsDto,
} from "@enos-monorepo/lib";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays, isSameDay, startOfToday, sub, subDays } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { DeleteShiftModal, Hours } from "../../components";
import { DayViewAddPastShiftModal } from "./Modals/DayViewAddPastShiftModal";

interface DayViewTableProps {
  selectedLocation?: CompanyLocation;
  companyId: string;
}

export function DayViewTable({
  selectedLocation,
  companyId,
}: DayViewTableProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [selectedDay, setSelectedDay] = useState(
    sub(startOfToday(), { days: 1 }),
  );
  const [selectedShift, setSelectedShift] = useState<Shift>();
  const [isDeleteShiftModalOpen, setIsDeleteShiftModalOpen] = useState(false);
  const [addPastShiftModalOpen, setAddPastShiftModalOpen] = useState(false);
  const [editShifts, setEditShifts] = useState<Shift[]>([]);

  const { startDate, endDate } = useMemo(
    () => getDayStartEnd(dateToISO(selectedDay)),
    [selectedDay],
  );

  const { data: shifts, isPending } = useQuery({
    queryKey: ["shifts", selectedLocation?.id, startDate, endDate],
    queryFn: () =>
      getShiftsByDate({
        LocationId: selectedLocation?.id ?? "",
        StartDate: dateToISO(startDate),
        EndDate: dateToISO(endDate),
      }),
    enabled: !!selectedLocation,
  });

  const { data: locationUsers } = useQuery({
    queryKey: ["companyLocations", selectedLocation?.id],
    queryFn: () => getLocationUsers(selectedLocation?.id ?? ""),
    enabled: !!selectedLocation,
  });

  const selectedDaysShifts = useMemo(() => {
    return shifts
      ?.filter(
        (shift) =>
          (shift.plannedStartDate &&
            new Date(shift.plannedStartDate) >= startDate &&
            new Date(shift.plannedStartDate) <= endDate &&
            shift.userId) ||
          (shift.workStartDate &&
            new Date(shift.workStartDate) >= startDate &&
            new Date(shift.workStartDate) <= endDate &&
            shift.userId),
      )
      .map((shift) => {
        return {
          ...shift,
          plannedStartDate: getHoursAndMinutes(shift.plannedStartDate),
          plannedEndDate: getHoursAndMinutes(shift.plannedEndDate),
          workStartDate: getHoursAndMinutes(shift.workStartDate),
          workEndDate: getHoursAndMinutes(shift.workEndDate),
        };
      });
  }, [endDate, shifts, startDate]);

  useEffect(() => {
    setEditShifts(selectedDaysShifts ?? []);
  }, [selectedDaysShifts]);

  const totalShiftAmount = useMemo(() => {
    return editShifts?.reduce((totalMinutes, shift) => {
      return (
        calculateShiftDuration(
          shift.workStartDate,
          shift.workEndDate,
          shift.breakDuration,
        ) + totalMinutes
      );
    }, 0);
  }, [editShifts]);

  const { mutate } = useMutation({
    mutationFn: (shift: UpdateBulkShift) => updateBulkShift(shift),
    onSuccess: () => {
      toast.success(t("common.The changes have been saved"));
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
    },
    onError: () => {
      toast.error(t("shift.Unable to plan shifts"));
    },
  });

  const onSave = (data: Shift[]) => {
    const bulkshifts: UpdateBulkShift = {
      updateShiftDtos: data.map((shift) => {
        const [startTime, endTime] = getNormalizedStartEnd(
          shift.plannedStartDate,
          shift.plannedEndDate,
          selectedDay,
        );

        const [workingStartTime, workingEndTime] = getNormalizedStartEnd(
          shift.workStartDate,
          shift.workEndDate,
          selectedDay,
        );

        return {
          id: shift.id,
          plannedStartTime: startTime ? startTime : shift.plannedStartDate,
          plannedEndTime: endTime ? endTime : shift.plannedEndDate,
          workStartDate: workingStartTime
            ? workingStartTime
            : shift.workStartDate,
          workEndDate: workingEndTime ? workingEndTime : shift.workEndDate,
          breakDuration: shift.breakDuration,
          actualPayment: shift.actualPayment,
          plannedPayment: shift.plannedPayment,
          companyId: companyId,
          companyLocationId: selectedLocation?.id ?? "",
          userId: shift.userId,
        };
      }) as UpdateShiftsDto[],
    };

    mutate(bulkshifts);
  };

  const dayViewColumns: DataTableColumn<Shift>[] = useMemo(
    () => [
      {
        header: t("common.Personnel"),
        accessorKey: "firstName",
        size: 225,
        cell: ({ row }) => (
          <Info
            userPhoto={row.original.userPhoto}
            name={row.original.firstName + " " + row.original.lastName}
            userName={row.original.userName}
          />
        ),
      },
      {
        header: t("shift.Planned Shift Data"),
        accessorKey: "plannedStartDate",
        cell: ({ row }) => (
          <div className="flex gap-2.5 flex-wrap">
            <Input
              value={row.original.plannedStartDate}
              size="sm"
              type="time"
              className="w-fit"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, plannedStartDate: e.target.value }
                      : shift,
                  ),
                );
              }}
            />
            <Input
              value={row.original.plannedEndDate}
              size="sm"
              className="w-fit"
              type="time"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, plannedEndDate: e.target.value }
                      : shift,
                  ),
                );
              }}
            />
          </div>
        ),
      },
      {
        header: t("shift.Actual Shift Data"),
        accessorKey: "workStartDate",
        cell: ({ row }) => (
          <div className="flex gap-2.5 flex-wrap">
            <Input
              value={row.original.workStartDate}
              size="sm"
              type="time"
              className="w-fit"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, workStartDate: e.target.value }
                      : shift,
                  ),
                );
              }}
            />
            <Input
              value={row.original.workEndDate}
              size="sm"
              className="w-fit"
              type="time"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, workEndDate: e.target.value }
                      : shift,
                  ),
                );
              }}
            />
          </div>
        ),
      },
      {
        header: t("shift.Total Break (Min)"),
        accessorKey: "breakDuration",
        cell: ({ row }) => (
          <div className="w-full">
            <Input
              placeholder="-"
              value={row.original.breakDuration?.toString()}
              size="sm"
              className="w-full"
              onChange={(e) => {
                setEditShifts((prev) =>
                  prev.map((shift) =>
                    shift.id === row.original.id
                      ? { ...shift, breakDuration: +e.target.value }
                      : shift,
                  ),
                );
              }}
            />
          </div>
        ),
      },
      {
        id: "totalAmount",
        header: t("shift.Total Amount"),
        cell: ({ row }) => (
          <div className="flex flex-row justify-center gap-2.5">
            <Hours>
              {formatDuration(
                calculateShiftDuration(
                  row.original.workStartDate,
                  row.original.workEndDate,
                  row.original.breakDuration,
                ),
              )}
            </Hours>
          </div>
        ),
      },
      {
        id: t("common.Actions"),
        size: 50,
        cell: () => (
          <div className="flex justify-center">
            <i
              onClick={() => setIsDeleteShiftModalOpen(true)}
              className="ki-duotone text-red-500 ki-trash-square text-xl cursor-pointer"
            ></i>
          </div>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <DataTable
        loading={isPending}
        header={
          <div className="flex gap-5">
            <div className="flex gap-5 items-center">
              <span
                className="flex items-center gap-1 cursor-pointer text-[#296573]"
                onClick={() => setAddPastShiftModalOpen(true)}
              >
                <i className="ki-filled ki-plus-squared"></i>
                <p className="text-xs">{t("shift.Add New")}</p>
              </span>
              <Button
                onClick={() => setEditShifts(selectedDaysShifts ?? [])}
                variant="outline"
                size="sm"
                property="danger"
              >
                {t("common.Discard Changes")}
              </Button>
              <Button
                variant="outline"
                onClick={() => onSave(editShifts)}
                size="sm"
              >
                {t("common.Save Changes")}
              </Button>
            </div>
            <div className="border-l-[1px] border-l-gray-300"></div>
            <div className="flex items-center gap-4">
              <Button
                size="xs"
                icon
                className="!bg-gray-300 flex justify-center !text-black"
                onClick={() => {
                  if (
                    JSON.stringify(editShifts) !==
                    JSON.stringify(selectedDaysShifts)
                  )
                    onSave(editShifts);
                  setSelectedDay((prev) => subDays(prev, 1));
                }}
              >
                <i className="ki-filled ki-left"></i>
              </Button>
              <DayPicker
                selectedDay={selectedDay}
                setSelectedDay={setSelectedDay}
                disabledDays={{ after: subDays(new Date(), 1) }}
              />
              <Button
                size="xs"
                icon
                className="!bg-gray-300 flex justify-center !text-black"
                disabled={isSameDay(selectedDay, subDays(new Date(), 1))}
                onClick={() => {
                  if (
                    JSON.stringify(editShifts) !==
                    JSON.stringify(selectedDaysShifts)
                  )
                    onSave(editShifts);
                  setSelectedDay((prev) => {
                    const nextDay = addDays(prev, 1);
                    return nextDay > subDays(new Date(), 1)
                      ? subDays(new Date(), 1)
                      : nextDay;
                  });
                }}
              >
                <i className="ki-filled ki-right"></i>
              </Button>
            </div>
          </div>
        }
        isInfoIcon={false}
        columns={dayViewColumns}
        data={editShifts}
        footer={
          <tr>
            <td>{t("shift.Total Amount")}</td>
            <td></td>
            <td></td>
            <td></td>
            <td className="flex justify-center">
              <Hours>{formatDuration(totalShiftAmount)}</Hours>
            </td>
            <td></td>
          </tr>
        }
        onRowClick={setSelectedShift}
        title={Intl.DateTimeFormat(i18n.language, {
          month: "short",
          day: "numeric",
          year: "numeric",
          weekday: "long",
        })
          .format(selectedDay)
          .replace(/(\d{4})/, "$1 /")}
      />
      <DeleteShiftModal
        open={isDeleteShiftModalOpen}
        onClose={() => setIsDeleteShiftModalOpen(false)}
        shift={selectedShift}
      />
      <DayViewAddPastShiftModal
        companyId={companyId}
        locationId={selectedLocation?.id ?? ""}
        open={addPastShiftModalOpen}
        onClose={() => setAddPastShiftModalOpen(false)}
        personnels={locationUsers ?? []}
        selectedDay={selectedDay}
      />
    </>
  );
}
